<template>
  <Portal>
    <Modal
      data-testid="impersonate-educational-modal"
      title="      Utilize a aba Catálogo"
      :is-open="isOpen"
      size="md"
      @close="handleClose"
    >
      <Heading :level="3" class="impersonate-educational-modal__title">
        Envie sugestões de investimento<br />
        diretamente pelo Flow!
      </Heading>

      <BaseText
        size="p4"
        :color="NebraskaColors.elementSecondary"
        class="impersonate-educational-modal__description"
      >
        Você pode realizar sugestões diretamente seguindo os <br />seguintes
        passos:
      </BaseText>
      <div class="impersonate-educational-modal__steps">
        <div
          v-for="(step, index) in steps"
          :key="index"
          class="impersonate-educational-modal__step"
        >
          <div class="impersonate-educational-modal__step-icon">
            <BaseIcon
              :icon="step.icon"
              :colors="STEP_ICON_COLOR"
              width="16px"
              height="16px"
            />
          </div>
          <div class="impersonate-educational-modal__step-content">
            <BaseText
              size="p4"
              :color="NebraskaColors.elementPrimary"
              class="impersonate-educational-modal__step-title"
            >
              {{ step.title }}
            </BaseText>
            <BaseText
              size="p5"
              :color="NebraskaColors.elementPrimary"
              class="impersonate-educational-modal__step-description"
            >
              {{ step.description }}
            </BaseText>
          </div>
        </div>
      </div>

      <div class="impersonate-educational-modal__disclaimer">
        <BaseIcon
          icon="EF0080"
          :colors="INFO_ICON_COLOR"
          width="16px"
          height="16px"
        />
        <div class="impersonate-educational-modal__disclaimer-content">
          <BaseText
            size="p4"
            :color="NebraskaColors.elementPrimary"
            class="impersonate-educational-modal__disclaimer-title"
          >
            Realize ações através do Flow
          </BaseText>
          <BaseText
            size="p5"
            :color="NebraskaColors.elementPrimary"
            class="impersonate-educational-modal__disclaimer-description"
          >
            Em breve não será mais possível realizar ações pelo cliente através
            do impersonamento.
          </BaseText>
        </div>
      </div>

      <template #buttonContainer>
        <ButtonContainer>
          <template #button-secondary>
            <ButtonPrimary label="Ir para o Flow" @click="handleGoToFlow" />
          </template>
          <template #button-primary>
            <ButtonSecondary
              label="Continuar como Cliente"
              @click="handleContinueAsClient"
            />
          </template>
        </ButtonContainer>
      </template>
    </Modal>
  </Portal>
</template>

<script setup lang="ts">
import {
  ButtonPrimary,
  ButtonSecondary,
  ButtonContainer,
  Modal,
  BaseText,
  Heading,
  BaseIcon
} from '@warrenbrasil/nebraska-web';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { Portal } from '@/modules/common/components/Portal';
import { logoutImpersonate } from '@/modules/common/services/partner';
import { getURLParams } from '@/modules/wallet-builder/src/helpers';

defineProps<{
  isOpen: boolean;
}>();

const emit = defineEmits(['close', 'continue']);

// Icon colors from Nebraska Design System
const STEP_ICON_COLOR = NebraskaColors.statusPositive;
const INFO_ICON_COLOR = NebraskaColors.statusInfo;

const steps = [
  {
    icon: 'EE0100',
    title: 'Acesse o Catálogo',
    description: 'Lá você encontra todos os ativos disponíveis.'
  },
  {
    icon: 'AA0040',
    title: 'Selecione o cliente',
    description: 'Encontre o cliente que deseja enviar a sugestão.'
  },
  {
    icon: 'EF0071',
    title: 'Finalize a sugestão',
    description: 'Ela será enviada automaticamente para aceite do seu cliente.'
  },
  {
    icon: 'DA0012',
    title: 'Acompanhe a Ordem',
    description: 'Você poderá ver tudo na aba Acompanhamento.'
  }
];

function handleClose() {
  emit('close');
}

function handleContinueAsClient() {
  emit('continue');
}

function handleGoToFlow() {
  const urlParams = getURLParams(true);

  if (urlParams?.backTo) {
    window.location.replace(urlParams.backTo);
    return;
  }

  logoutImpersonate();
}
</script>

<style lang="less" scoped>
.impersonate-educational-modal {
  &__title {
    text-align: center;
    font-weight: 600;
    margin-bottom: @size-spacing-x500;
  }

  &__description {
    text-align: center;
    line-height: 21px;
    margin-bottom: @size-spacing-x500;
  }

  &__steps {
    display: flex;
    flex-direction: column;
    gap: @size-spacing-x450;
    margin-bottom: @size-spacing-x500;
    width: 100%;
  }

  &__step {
    display: flex;
    gap: @size-spacing-x400;
    align-items: flex-start;
  }

  &__step-icon {
    width: 42px;
    height: 42px;
    border: 1px solid @divider-primary;
    border-radius: @size-radius-x200;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__step-content {
    display: flex;
    flex-direction: column;
    gap: @size-spacing-x150;
    flex: 1;
    align-items: flex-start;
  }

  &__step-title {
    font-weight: 600;
  }

  &__step-description {
    line-height: 16px;
  }

  &__disclaimer {
    display: flex;
    gap: @size-spacing-x300;
    padding: @size-spacing-x350;
    background: @status-info-background;
    border-radius: @size-radius-x200;
    align-items: flex-start;
  }

  &__disclaimer-content {
    display: flex;
    flex-direction: column;
    gap: @size-spacing-x150;
    flex: 1;
    align-items: flex-start;
  }

  &__disclaimer-title {
    font-weight: 600;
  }

  &__disclaimer-description {
    line-height: 16px;
    text-align: left;
  }
}

:deep(.modal-content) {
  padding: @size-spacing-x700;
  align-items: center;
}
</style>
